import {ChevronRightIcon} from '@heroicons/react/outline';
import React, {useEffect, useState} from 'react';

import ActionIcons from '@/components/AppReleases/ActionIcons';
import PlatformIcon from '@/components/AppReleases/PlatformIcon';
import ReleaseNotesDisplay from '@/components/AppReleases/ReleaseNotesDisplay';
import VersionBadge from '@/components/AppReleases/VersionBadge';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import {AppReleasesService} from '@/services/appReleasesService';
import type {
  AppDistributionRelease,
  AppReleasesState,
} from '@/types/FirebaseAppDistribution';
import {AppPlatform} from '@/types/FirebaseAppDistribution';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';

interface AppReleasesTableProps {
  platform: AppPlatform;
  onReleaseSelect: (release: AppDistributionRelease) => void;
}

const AppReleasesTable: React.FC<AppReleasesTableProps> = ({
  platform,
  onReleaseSelect,
}) => {
  const [state, setState] = useState<AppReleasesState>({
    releases: [],
    loading: true,
    error: null,
    nextPageToken: null,
    hasMore: false,
    platform,
    selectedRelease: null,
  });

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Load releases when platform changes
  useEffect(() => {
    setState((prev) => ({...prev, loading: true}));
    // `loadReleases` try/catches into `state.error`, so this only fires if that
    // catch block itself throws. Sent to the dev logger rather than back into
    // the same setState that would have just failed.
    loadReleases(true).catch(logger.error);
  }, [platform]);

  const loadReleases = async (reset = false) => {
    try {
      setState((prev) => ({
        ...prev,
        loading: reset,
        error: null,
        platform,
      }));

      const response = await AppReleasesService.getInstance().getAppReleases({
        platform,
        nextPageToken: reset ? undefined : state.nextPageToken || undefined,
      });

      setState((prev) => ({
        ...prev,
        releases: reset
          ? response.releases
          : [...prev.releases, ...response.releases],
        nextPageToken: response.nextPageToken || null,
        hasMore: !!response.nextPageToken,
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: getErrorMessage(error, 'Failed to load releases'),
        loading: false,
      }));
    }
  };

  const loadMore = async () => {
    if (isLoadingMore || !state.hasMore) {
      return;
    }

    setIsLoadingMore(true);
    await loadReleases(false);
    setIsLoadingMore(false);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleNotesToggle = (releaseId: string, isExpanded: boolean) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(releaseId);
      } else {
        newSet.delete(releaseId);
      }
      return newSet;
    });
  };

  if (state.loading && state.releases.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <PlatformIcon platform={platform} size="sm" />
          <span className="font-medium">
            Loading {platform === AppPlatform.IOS ? 'iOS' : 'Android'}{' '}
            Releases...
          </span>
        </div>
        <TableSkeleton />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="text-red-800 dark:text-red-200 text-sm">
          {state.error}
        </div>
        <button
          onClick={() => loadReleases(true)}
          className="mt-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 text-sm underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Platform Header */}
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <PlatformIcon platform={platform} size="sm" />
        <span className="font-medium">
          {platform === AppPlatform.IOS ? 'iOS' : 'Android'} Releases
        </span>
        {state.releases.length > 0 && (
          <span className="text-gray-400 dark:text-gray-500">
            ({state.releases.length} releases)
          </span>
        )}
      </div>

      {/* Releases Table */}
      {state.releases.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="text-gray-500 dark:text-gray-400">
            No releases found.
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-transparent shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-600 overflow-auto w-full">
            {state.releases.map((release, index) => {
              const isExpanded = expandedRows.has(release.name);
              const zebraRow =
                index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

              return (
                <li
                  key={release.name}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 ${zebraRow}`}
                  style={{
                    minHeight: isExpanded ? '120px' : '80px',
                    height: 'auto',
                  }}
                >
                  <div className="px-6 py-4 flex items-start justify-between">
                    <div className="flex items-start space-x-5 flex-1 min-h-0">
                      <div className="flex-shrink-0 mt-1">
                        <VersionBadge
                          version={release.displayVersion}
                          buildVersion={release.buildVersion}
                        />
                      </div>

                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            Build {release.buildVersion}
                          </p>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(release.createTime)}
                        </p>

                        {/* Release Notes */}
                        {release.releaseNotes?.text && (
                          <div className="mt-2">
                            <ReleaseNotesDisplay
                              text={release.releaseNotes.text}
                              onToggle={(expanded) =>
                                handleNotesToggle(release.name, expanded)
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 ml-4">
                      <ActionIcons
                        binaryDownloadUri={release.binaryDownloadUri}
                        firebaseConsoleUri={release.firebaseConsoleUri}
                        testingUri={release.testingUri}
                        fileName={`${release.displayVersion}-${release.buildVersion}`}
                      />

                      <button
                        onClick={() => onReleaseSelect(release)}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1 transition-colors duration-150"
                        title="View Details"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Load More Button */}
          {state.hasMore && (
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 text-center border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? 'Loading...' : 'Load More Releases'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppReleasesTable;
